import { Address, erc20Abi, formatUnits } from 'viem';
import { QueryOptions } from 'utils/useQueryUtils';
import { usePublicClient } from 'wagmi';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DynaVault } from '../../../../api/DynaVaultsAPI';
import { DynaVaultABI } from 'utils/constants/abi/DynaVaultABI';
import { MulticallReturnType } from 'utils/types';
import { getVaultRegistryAddress } from 'sfi-vault-sdk';
import { getOracleDeployment } from 'sfi-oracle-sdk';
import {
  DynaRouterRegistryABI,
  getRouterRegistryAddress,
} from 'sfi-router-sdk';

interface UseVaulttBlockchainProps extends QueryOptions {
  address: `0x${string}`;
  chainId: number;
}

export const useVaultBlockchain = (options: UseVaulttBlockchainProps) => {
  const { address, query, chainId } = options;
  const publicClient = usePublicClient({ chainId });
  const oracleDeployment = getOracleDeployment(chainId);

  const useQueryOptions = {
    queryKey: queryKeys.vaultBlockchain({
      chainId,
      address,
    }),
    placeholderData: keepPreviousData,
    ...query,
  };

  return useQuery<DynaVault & { tvlUsd: string }>({
    ...useQueryOptions,
    queryFn: async () => {
      if (!publicClient) {
        throw new Error('Public client not available');
      }
      if (!oracleDeployment) {
        throw new Error(`No oracle deployment found for chainId ${chainId}`);
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const vaultDataResponses = await publicClient
        .multicall({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          contracts: [
            {
              address: address,
              abi: DynaVaultABI,
              functionName: 'symbol',
            },
            {
              address: address,
              abi: DynaVaultABI,
              functionName: 'name',
            },
            {
              address: address,
              abi: DynaVaultABI,
              functionName: 'manager',
            },
            {
              address: address,
              abi: DynaVaultABI,
              functionName: 'totalAssets',
            },
            {
              address: address,
              abi: erc20Abi,
              functionName: 'decimals',
            },
            {
              address: address,
              abi: DynaVaultABI,
              functionName: 'asset',
            },
            {
              address: address,
              abi: DynaVaultABI,
              functionName: 'routerRegistry',
            },
            {
              address: address,
              abi: DynaVaultABI,
              functionName: 'referenceAsset',
            },
          ],
          authorizationList: undefined,
        })
        .catch((error) => {
          console.error('Multicall for vault data failed:', error);
          return [];
        });

      const [
        symbol,
        name,
        managementWallet,
        totalAssets,
        decimals,
        asset,
        routerRegistry,
        referenceAsset,
      ] = vaultDataResponses as [
        MulticallReturnType<string>,
        MulticallReturnType<string>,
        MulticallReturnType<Address>,
        MulticallReturnType<bigint>,
        MulticallReturnType<number>,
        MulticallReturnType<Address>,
        MulticallReturnType<Address>,
        MulticallReturnType<Address>,
      ];

      const routerRegistryAddress =
        routerRegistry.result ?? getRouterRegistryAddress(chainId);
      if (!routerRegistryAddress) {
        throw new Error(`No router registry found for chainId ${chainId}`);
      }

      const [vaultZapperAddress, tvlUsdResponse, quoteAssetDecimalsResponse] =
        await publicClient.multicall({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          contracts: [
            {
              abi: DynaRouterRegistryABI,
              address: routerRegistryAddress,
              functionName: 'getDefaultTokenRouter',
              args: [address],
            },
            {
              address: address,
              abi: DynaVaultABI,
              functionName: 'tokenValueInQuoteAsset',
              args: [
                referenceAsset.result as `0x${string}`,
                totalAssets.result as bigint,
                oracleDeployment.referenceAsset,
              ],
            },
            {
              address: oracleDeployment.referenceAsset,
              abi: erc20Abi,
              functionName: 'decimals',
            },
          ],
          authorizationList: undefined,
        });

      const tvlUsd = formatUnits(
        (tvlUsdResponse.result as bigint) ?? BigInt(0),
        (quoteAssetDecimalsResponse.result as number) ?? 0
      );

      return {
        id: 1,
        chainId: chainId,
        name: name.result || '',
        symbol: symbol.result || '',
        decimals: decimals.result || 0,
        address: address,
        managementFees: 0,
        performanceFee: 0,
        ticker: '',
        factory: '',
        activation: '1999-12-31T23:00:00.000Z',
        managementWallet: managementWallet.result || '',
        governanceWallet: '',
        guardianWallet: '',
        functioningType: 'DynaVault', // ??
        isClosed: false,
        sinceInceptionAPY: 0,
        twentyFourHourAPY: 0,
        weeklyAPY: 0,
        monthlyAPY: 0,
        yearlyAPY: 0,
        fees: [],
        apr: '0',
        type: 'YIELD',
        depositToken: asset.result || '0x00', // ??
        asset: asset.result || '0x00', // ??
        riskLevel: 'HIGH',
        description: '',
        factsheets: [],
        iconUrl: '',
        twentyFourHourPerformance: '0',
        weeklyPerformance: '0',
        monthlyPerformance: '0',
        yearlyPerformance: '0',
        sinceInceptionPerformance: '0',
        netPerformance: '0',
        lastReport: '0',
        lastComputed: '1999-12-31T23:00:00.000Z',
        totalSupply: (totalAssets.result ?? BigInt(0)).toString(),
        tvlUsd: tvlUsd.toString(),
        price: 0, // ?
        availableFunds: '0',
        deployedFunds: '0',
        resourceId: 1,
        createdAt: '1999-12-31T23:00:00.000Z',
        updatedAt: '1999-12-31T23:00:00.000Z',
        userWalletAddress: '',
        metadata: {
          contracts: {
            type: 'DynaVault',
            DynaVault: address,
            DynaRouter: routerRegistryAddress,
            VaultZapper: vaultZapperAddress.result || '0x0',
            dynaLiquidity: '0x0',
            BalancerV2Zapper: '0x0',
            DynaVaultManager: '0x0',
          },
        },
      };
    },
    enabled:
      !!publicClient &&
      !!getVaultRegistryAddress(chainId) &&
      !!oracleDeployment,
  });
};
